import { useState } from 'react'
import { useStore } from '@tanstack/react-store'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Empty, EmptyMedia, EmptyDescription } from '@/components/ui/empty'
import {
  Table as TableUI,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TaskStatusEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getTaskStatusColor, getParentTypeColor } from '@/lib/enum-utils'
import { formatTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { formatFullName } from '@/lib/utils'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { useUpdateTask } from '@/stores/tasks.store'
import { useSystemSettings } from '@/stores/settings.store'
import { organizationStore } from '@/stores/organization.store'
import { TaskSheet } from '@/components/tasks/task-sheet'
import { Table as TableIcon, Pencil, Loader2 } from 'lucide-react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table'
import type { Task, TaskLite } from '@/gen/cortado/v1/actions_pb'
import { TasksTableToolbar } from './tasks-table-toolbar'
import { DataTableColumnHeader } from '@/components/shared/data-table/column-header'

interface LeadTasksTableProps {
  data: TaskLite[]
}

/**
 * Inline status selector — same badge + popover pattern as the dashboard task card,
 * so a status can be changed without opening the task.
 */
function TaskStatusCell({ task }: { task: TaskLite }) {
  const [isOpen, setIsOpen] = useState(false)
  const { settings } = useSystemSettings()
  const { updateTaskStatus, isLoading } = useUpdateTask()

  const statusOptions = (settings?.taskStatuses || []).filter(
    (status) => status !== TaskStatusEnum.TASK_STATUS_UNKNOWN
  )

  const handleStatusChange = async (newStatus: TaskStatusEnum) => {
    setIsOpen(false)
    if (newStatus === task.taskStatus) return

    try {
      await updateTaskStatus(task.id, newStatus)
      toast.success('Task status updated')
    } catch (error) {
      reportError(error, { feature: 'task_status_update', kind: 'mutation' })
      toast.error('Failed to update task status', {
        description: getErrorDescription(error),
      })
    }
  }

  return (
    <div className="flex justify-left">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={isLoading}>
            <Badge
              variant={getTaskStatusColor(task.taskStatus)}
              className="cursor-pointer"
>
              {isLoading && <Loader2 className="h-3 w-3 animate-spin" />}
              {formatEnumName(task.taskStatus, TaskStatusEnum)}
            </Badge>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-2" align="start">
          <div className="space-y-1">
            {statusOptions.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => handleStatusChange(status)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                  task.taskStatus === status
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {formatEnumName(status, TaskStatusEnum)}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

/** Pencil action that opens the shared TaskSheet in edit mode. */
function TaskActionsCell({ task }: { task: TaskLite }) {
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  // Mount the sheet lazily (one per row would be wasteful), but keep it mounted
  // afterwards so the close animation can play.
  const [isSheetMounted, setIsSheetMounted] = useState(false)
  const selectedOrganizationId = useStore(
    organizationStore,
    (state) => state.selectedOrganizationId
  )

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => {
              setIsSheetMounted(true)
              setIsSheetOpen(true)
            }}
          >
            <span className="sr-only">Edit task</span>
            <Pencil className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left" className="text-xs">
          Edit task
        </TooltipContent>
      </Tooltip>
      {isSheetMounted && selectedOrganizationId && (
        <TaskSheet
          organizationId={selectedOrganizationId}
          parentType={task.parentType}
          parentId={task.parentId}
          task={task as unknown as Task}
          open={isSheetOpen}
          onOpenChange={setIsSheetOpen}
        />
      )}
    </>
  )
}

const columns: ColumnDef<TaskLite>[] = [
  {
    accessorKey: 'title',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Title" />
    ),
    cell: ({ row }) => {
      return <div className="font-medium">{row.original.title}</div>
    },
  },
  {
    accessorKey: 'taskStatus',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => <TaskStatusCell task={row.original} />,
    filterFn: (row, id, value) => {
      return value.includes(String(row.getValue(id)))
    },
  },
  {
    accessorKey: 'parentType',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source" />
    ),
    cell: ({ row }) => {
      const parentType = row.original.parentType as ParentTypeEnum
      return (
        <div className="flex justify-left">
          <Badge variant={getParentTypeColor(parentType)}>
            {formatEnumName(parentType, ParentTypeEnum)}
          </Badge>
        </div>
      )
    },
  },
  {
    accessorKey: 'dueDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Due Date" />
    ),
    cell: ({ row }) => {
      const dueDate = row.original.dueDate
      if (!dueDate) {
        return (
          <div className="text-left text-sm text-muted-foreground">
            No due date
          </div>
        )
      }

      // Check if date is invalid (like Dec 31, 0001 - year < 1900)
      const seconds = typeof dueDate.seconds === 'bigint'
        ? Number(dueDate.seconds)
        : dueDate.seconds
      const date = new Date(seconds * 1000)
      const year = date.getFullYear()

      // If year is before 1900, consider it invalid
      if (year < 1900) {
        return (
          <div className="text-left text-sm text-muted-foreground">
            -
          </div>
        )
      }

      return (
        <div className="text-left text-sm text-muted-foreground">
          {formatTimestamp(timestampToDateOnly(dueDate))}
        </div>
      )
    },
    sortingFn: (rowA, rowB) => {
      const dateA = rowA.original.dueDate
      const dateB = rowB.original.dueDate
      if (!dateA && !dateB) return 0
      if (!dateA) return 1
      if (!dateB) return -1

      const secondsA = typeof dateA.seconds === 'bigint' ? Number(dateA.seconds) : dateA.seconds
      const secondsB = typeof dateB.seconds === 'bigint' ? Number(dateB.seconds) : dateB.seconds

      // Check if dates are invalid (year < 1900)
      const dateObjA = new Date(secondsA * 1000)
      const dateObjB = new Date(secondsB * 1000)
      const yearA = dateObjA.getFullYear()
      const yearB = dateObjB.getFullYear()

      const invalidA = yearA < 1900
      const invalidB = yearB < 1900

      if (invalidA && invalidB) return 0
      if (invalidA) return 1  // Invalid dates sort to the end
      if (invalidB) return -1

      return secondsA - secondsB
    },
  },
  {
    accessorKey: 'assignedTo',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Assigned To" />
    ),
    cell: ({ row }) => {
      const assignedToName = formatFullName(
        row.original.assignedToFirstName,
        row.original.assignedToLastName,
        'Unassigned'
      )
      return <div className="text-sm">{assignedToName}</div>
    },
    sortingFn: (rowA, rowB) => {
      const nameA = formatFullName(
        rowA.original.assignedToFirstName,
        rowA.original.assignedToLastName,
        'Unassigned'
      )
      const nameB = formatFullName(
        rowB.original.assignedToFirstName,
        rowB.original.assignedToLastName,
        'Unassigned'
      )
      return nameA.localeCompare(nameB)
    },
    filterFn: (row, _id, value) => {
      const assignedToId = row.original.assignedTo
      if (!assignedToId) {
        return false
      }
      return value.includes(assignedToId)
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => <TaskActionsCell task={row.original} />,
  },
]

export function LeadTasksTable({ data }: LeadTasksTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      sorting,
      columnFilters,
    },
  })

  if (data.length === 0) {
    return (
      <Empty className="min-h-[200px]">
        <EmptyMedia variant="icon">
          <TableIcon className="h-10 w-10" />
        </EmptyMedia>
        <EmptyDescription>
          No tasks to display
        </EmptyDescription>
      </Empty>
    )
  }

  return (
    <div className="space-y-4">
      <TasksTableToolbar table={table} tasks={data} />
      <div className="rounded-lg border bg-card">
        <TableUI>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TableUI>
      </div>
    </div>
  )
}

